import {
  City,
  Country,
  FranceTravailExperienceLevel,
  FranceTravailSalaryPeriod,
  ROMEJob,
  WishedContractTypes,
} from '@addviseo/shared/types';

export enum OpportunityType {
  JOB_OFFER = 'jobOffer',
  EVENT = 'event',
  CONTACT = 'contact',
}

export const OPPORTUNITY_TYPES: readonly OpportunityType[] = [
  OpportunityType.JOB_OFFER,
  OpportunityType.EVENT,
  OpportunityType.CONTACT,
] as const;

export enum EventModality {
  PRESENTIEL = 'presentiel',
  DISTANCIEL = 'distanciel',
  MIXTE = 'mixte',
}

export enum ContactRecruitingPattern {
  RECRUIT_REGULARLY = 'recrute_regulierement',
  SHARE_EXPERTISE = 'partage_expertise',
  WELCOME_PMSMP = 'accueille_pmsmp',
}

interface OpportunityBase {
  id: string;
  lastUpdatedAt: string;
}

export interface JobOfferOpportunity extends OpportunityBase {
  type: OpportunityType.JOB_OFFER;
  title: string;
  description: string;
  company: { name: string; logo: string };
  location: { label: string };
  contractType: string;
  contractTypeLabel: string;
  source: { url: string };
  romeCode: string;
  job: string | null;
  weeklyTime?: number;
  /** France Travail sends a human-readable label, Prosaisie a raw number. */
  salary?: number | string;
  qualification?: string;
  sectorOfActivity?: string;
  desiredExperience?: string;
  formations?: string[];
  skills?: string[];
  languages?: string[];
  permits?: string[];
  professionalQualities?: string[];
}

export interface EventOpportunity extends OpportunityBase {
  type: OpportunityType.EVENT;
  title: string;
  description: string;
  /** ISO date, YYYY-MM-DD */
  date: string;
  /** HH:mm, required */
  startTime: string;
  /** HH:mm, optional */
  endTime: string | null;
  modality: EventModality;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  job: string | null;
  visioLink: string | null;
  eventLink: string | null;
}

export interface ContactOpportunity extends OpportunityBase {
  type: OpportunityType.CONTACT;
  title: string;
  firstName: string;
  lastName: string;
  description: string;
  email: string;
  job: string | null;
  phoneNumber: string | null;
  postalCode: string | null;
  city: string | null;
  contactLink: string | null;
  recruitingPattern: ContactRecruitingPattern | null;
  recruitingJob: string | null;
}

export type Opportunity = JobOfferOpportunity | EventOpportunity | ContactOpportunity;

export const OPPORTUNITY_DISTANCE_OPTIONS = [
  0, 5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100,
] as const;
export type OpportunityDistance = (typeof OPPORTUNITY_DISTANCE_OPTIONS)[number];
export const DEFAULT_OPPORTUNITY_DISTANCE: OpportunityDistance = 10;

export enum OpportunitiesSource {
  FRANCE_TRAVAIL = 'france-travail',
  PROSAISIE = 'prosaisie',
}

export const DEFAULT_OPPORTUNITIES_SOURCE: OpportunitiesSource = OpportunitiesSource.FRANCE_TRAVAIL;
export const DEFAULT_OPORTUNITIES_TYPES: OpportunityType[] = [OpportunityType.JOB_OFFER];

export const MAX_ROME_CODES_PER_FT_QUERY = 200;

export interface OpportunityMinSalary {
  amount: number;
  period: FranceTravailSalaryPeriod;
}

export interface OpportunitiesAdvancedFilters {
  domains: string[];
  contractType: WishedContractTypes[];
  experienceLevels: FranceTravailExperienceLevel[];
  minSalary: OpportunityMinSalary | null;
}

export const createDefaultOpportunitiesAdvancedFilters = (): OpportunitiesAdvancedFilters => ({
  domains: [],
  contractType: [],
  experienceLevels: [],
  minSalary: null,
});

export const countActiveOpportunitiesAdvancedFilters = (
  filters: OpportunitiesAdvancedFilters,
): number =>
  [
    filters.domains.length > 0,
    filters.contractType.length > 0,
    filters.experienceLevels.length > 0,
    filters.minSalary !== null,
  ].filter(Boolean).length;

export interface ListOpportunitiesFilters {
  jobs: ROMEJob[];
  city: City | null;
  country: Country | null;
  distance: OpportunityDistance;
  types?: OpportunityType[];
  contractType?: string[];
  domains?: string[];
  experienceLevels?: FranceTravailExperienceLevel[];
  minSalary?: OpportunityMinSalary | null;
}

export interface ListOpportunitiesQuery {
  source?: OpportunitiesSource;
  page: number;
  rowsPerPage: number;
  search?: string;
  filters: ListOpportunitiesFilters;
}

export interface ListOpportunitiesResponse {
  offers: Opportunity[];
  pagination: {
    range: string;
    nextRange: string | null;
    totalCount: number;
    hasMore: boolean;
    countByType: Record<'JobOffer' | 'Event' | 'Contact', number> | null;
  };
}

export const BENEFICIARY_OFFERS_PER_PAGE = 20;
export const ADVISOR_OFFERS_PER_PAGE = 50;

export const mapPageToRange = (page: number, rowsPerPage: number): string => {
  const start = (page - 1) * rowsPerPage;
  const end = start + rowsPerPage - 1;
  return `${start}-${end}`;
};
